import { Button, Icon, LoadingDots } from '@advanza/ui'
import NiceModal from '@ebay/nice-modal-react'
import { fetchProvider } from 'actions/providers'
import { cancelScheduledTermination } from 'actions/scheduledTerminations'
import Card from 'components/Card'
import ScheduleTerminationModal from 'components/providers/ScheduleTerminationModal'
import { getChurnReasonDisplayText } from 'components/reports/common/constants'
import { utcToLocal } from 'date'
import { useAppDispatch } from 'hooks/hooks'
import { useProvider } from 'hooks/providerHooks'
import { hasAccess, isSuperUser } from 'misc/user'
import { useState } from 'react'

interface Props {
    providerId: number
}

const ScheduledTerminationCard = ({ providerId }: Props) => {
    const dispatch = useAppDispatch()
    const { provider } = useProvider(providerId)
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const scheduledTermination = provider?.scheduledTermination

    if (!provider || (!scheduledTermination && !provider.isSigned)) {
        return null
    }

    const canEdit = isSuperUser() || hasAccess('providers', 'w')

    const onCancelSchedule = () => {
        if (isSaving) {
            return
        }

        setIsSaving(true)
        setError(null)
        cancelScheduledTermination(providerId)
            .then(() => dispatch(fetchProvider(providerId, true)))
            .catch((err: unknown) =>
                setError(
                    err instanceof Error ? err.message : 'Failed to cancel scheduled termination',
                ),
            )
            .finally(() => setIsSaving(false))
    }

    const onSchedule = () =>
        NiceModal.show(ScheduleTerminationModal, {
            providerId,
            onScheduled: () => dispatch(fetchProvider(providerId, true)),
        })

    return (
        <Card icon="event_busy" header="Scheduled termination">
            <div className="mt-3">
                {scheduledTermination ? (
                    <>
                        <div className="p-2 bg-tertiary-50 border border-tertiary-200 rounded-md">
                            <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                                <div className="font-bold">Termination date:</div>
                                <div className="flex items-center gap-1 font-bold text-tertiary-500">
                                    <Icon name="event_busy" size="sm" orange />
                                    {utcToLocal(
                                        scheduledTermination.termination_date,
                                        'd MMMM yyyy',
                                    )}
                                </div>
                                <div className="font-bold">Reason:</div>
                                <div>{getChurnReasonDisplayText(scheduledTermination.reason)}</div>
                                {scheduledTermination.explanation && (
                                    <>
                                        <div className="font-bold">Note:</div>
                                        <div>{scheduledTermination.explanation}</div>
                                    </>
                                )}
                            </div>
                        </div>
                        <p className="mt-2 text-md text-grey-700">
                            The provider stays fully active until then. On the termination date
                            their subscription is paused, lead sending stops and their profile is
                            deactivated.
                        </p>
                        {canEdit && (
                            <Button
                                className="mt-3"
                                appearance="outlined_primary"
                                disabled={isSaving}
                                onClick={onCancelSchedule}>
                                {isSaving ? <LoadingDots /> : 'Cancel scheduled termination'}
                            </Button>
                        )}
                        {error && <div className="mt-2 text-md text-error">{error}</div>}
                    </>
                ) : (
                    <>
                        <p className="text-md text-grey-700">
                            For providers who asked to churn. Pick today to terminate them straight
                            away, or a future date to let them run until then.
                        </p>
                        {canEdit && (
                            <Button
                                className="mt-3"
                                appearance="outlined_primary"
                                onClick={onSchedule}>
                                Schedule termination
                            </Button>
                        )}
                    </>
                )}
            </div>
        </Card>
    )
}

export default ScheduledTerminationCard
